import {otsBaseApi} from "../../ots-api";
import {Pipeline, PipelineSearchCriteria} from "../../../model/pipeline/pipeline";
import {removeUndefined} from "../../../util/util";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Pipelines"]});

const pipelineApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllPipelines: builder.query<Pipeline[], string | undefined>({
      query: (regionId) => ({
        url: `/v2/pipeline/all`,
        method: "GET",
        params: buildParams(regionId)
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Pipelines", id} as const)),
            {type: "Pipelines", id: "LIST"}
          ];
        } else {
          return [{type: "Pipelines", id: "LIST"}];
        }
      }
    }),
    getPipelinesByObject: builder.query<Pipeline[], {
      regionId?: string,
      objectId: string,
      objectType: string,
      dttm?: number
    }>({
      query: (arg) => ({
        url: `/v2/pipeline/object`,
        method: "GET",
        params: removeUndefined({
          regionId: arg.regionId,
          objectId: arg.objectId,
          objectType: arg.objectType,
          dttm: arg.dttm
        })
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Pipelines", id} as const))
          ];
        } else {
          return [];
        }
      }
    }),
    getPipeline: builder.query<Pipeline, string>({
      query: (id) => ({
        url: `/v2/pipeline/${id}`,
        method: "GET",
        params: buildParams()
      }),
      providesTags: (result, error, id) => [{type: "Pipelines", id}]
    }),
    searchPipelines: builder.query<
      Pipeline[],
      PipelineSearchCriteria
    >({
      query: (criteria) => ({
        url: `/v2/pipeline/search`,
        method: 'POST',
        body: criteria,
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Pipelines", id} as const)),
            {type: "Pipelines", id: "LIST"}
          ];
        } else {
          return [{type: "Pipelines", id: "LIST"}];
        }
      },
    }),
    createPipeline: builder.mutation<Pipeline, Partial<Pipeline>>({
      query: (mapping) => ({
        url: "/v2/pipeline",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [{type: "Pipelines", id: "LIST"}]
    }),
    updatePipeline: builder.mutation<Pipeline, Partial<Pipeline>>({
      query: (mapping) => ({
        url: "/v2/pipeline",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [{type: "Pipelines", id}]
    }),
    deletePipeline: builder.mutation<void, Partial<Pipeline>>({
      query: (mapping) => ({
        url: "/v2/pipeline",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [{type: "Pipelines", id}]
    })
  })
});

const buildParams = (regionId?: string) => {
  const extrasParams = {
    withLevelData: false,
    withPipelineData: true
  };

  if (regionId) {
    return {
      regionId: regionId,
      ...extrasParams
    };
  } else {
    return extrasParams;
  }
};

export const {
  useGetAllPipelinesQuery,
  useGetPipelinesByObjectQuery,
  useGetPipelineQuery,
  useSearchPipelinesQuery,
  useCreatePipelineMutation,
  useUpdatePipelineMutation,
  useDeletePipelineMutation,
} = pipelineApi;
